import { FOCO_VISIVEL } from 'utils/foco';

/** Páginas que aparecem de cada lado da atual. */
const VIZINHAS = 1;
/** Até este total, todas as páginas aparecem, sem reticências. */
const TODAS_ATE = 5;

const NUMERO = `h-[34px] min-w-[34px] cursor-pointer rounded-lg border-0 px-2.5 font-display text-[13px] font-bold transition-colors ${FOCO_VISIVEL}`;

// `aria-disabled` no lugar de `disabled`: a seta inativa continua no Tab e o leitor de tela anuncia o estado.
const SETA = `flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-brand-white text-brand-blue shadow-jj-01 transition hover:bg-brand-blue hover:text-brand-yellow aria-disabled:cursor-default aria-disabled:opacity-40 aria-disabled:hover:bg-brand-white aria-disabled:hover:text-brand-blue ${FOCO_VISIVEL}`;

interface PaginacaoProps {
  /** Total de páginas. Abaixo de 2, a paginação não aparece (PAD-01). */
  total: number;
  /** Página atual, a partir de 0. */
  atual: number;
  onChange: (pagina: number) => void;
  /** Nome do `nav`, quando a tela tem um mais específico ("Paginação dos funcis participantes"). */
  rotulo?: string;
  /** Posição do `nav` na tela. Padrão: centralizado, abaixo da lista. */
  className?: string;
}

/**
 * Páginas visíveis (PAD-02): a primeira, a última, a atual e as vizinhas; os saltos viram reticências. Com
 * dados reais há listas com 180+ páginas, e sem a janela a fila empurrava as setas para fora da tela.
 */
export function janelaDePaginas(total: number, atual: number): Array<number | 'salto'> {
  if (total <= TODAS_ATE) return Array.from({ length: total }, (_, pagina) => pagina);

  const paginas = new Set<number>([0, total - 1]);
  for (let pagina = atual - VIZINHAS; pagina <= atual + VIZINHAS; pagina++) {
    if (pagina >= 0 && pagina < total) paginas.add(pagina);
  }

  const ordenadas = Array.from(paginas).sort((a, b) => a - b);
  return ordenadas.flatMap((pagina, indice) => {
    const anterior = ordenadas[indice - 1];
    if (indice === 0 || pagina - anterior === 1) return [pagina];
    // Uma página só no salto aparece no lugar das reticências: "…" esconderia exatamente um número.
    return pagina - anterior === 2 ? [pagina - 1, pagina] : ['salto' as const, pagina];
  });
}

function Seta({
  direcao,
  desabilitada,
  onClick,
}: {
  direcao: 'anterior' | 'proxima';
  desabilitada: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={direcao === 'anterior' ? 'Página anterior' : 'Próxima página'}
      aria-disabled={desabilitada || undefined}
      onClick={desabilitada ? undefined : onClick}
      className={SETA}
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d={direcao === 'anterior' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
      </svg>
    </button>
  );
}

/**
 * Paginação única da nova identidade (PAD-01 a PAD-04), do `Galeria.dc.html`: números em quadrados de cantos
 * arredondados e setas redondas. A página atual vai em `brand-blue-dark` com o número amarelo: o azul do design
 * com o amarelo dava 4,44:1.
 */
export default function Paginacao({
  total,
  atual,
  onChange,
  rotulo = 'Navegação de páginas',
  className = 'mt-[26px] justify-center',
}: PaginacaoProps) {
  if (total < 2) return null;
  // A lista pode encolher sem a tela voltar a página (uma busca que reduz o total): a atual fica na última.
  const pagina = Math.min(Math.max(atual, 0), total - 1);

  return (
    <nav aria-label={rotulo} className={`flex flex-wrap items-center gap-2 ${className}`}>
      <Seta direcao="anterior" desabilitada={pagina <= 0} onClick={() => onChange(pagina - 1)} />

      {janelaDePaginas(total, pagina).map((item, indice) =>
        item === 'salto' ? (
          <span key={`salto-${indice}`} aria-hidden="true" className="px-1 font-display text-sm font-bold text-content-soft">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            aria-label={`Ir para a página ${item + 1}`}
            aria-current={item === pagina ? 'page' : undefined}
            onClick={() => onChange(item)}
            className={`${NUMERO} ${
              item === pagina
                ? 'bg-brand-blue-dark text-brand-yellow'
                : 'bg-brand-white text-brand-blue hover:bg-surface-100 hover:text-brand-blue-dark'
            }`}
          >
            {item + 1}
          </button>
        ),
      )}

      <Seta direcao="proxima" desabilitada={pagina >= total - 1} onClick={() => onChange(pagina + 1)} />
    </nav>
  );
}
